/**
 * The chip. It RENDERS ENGINE OUTPUT AND NOTHING ELSE.
 *
 *   MathChip     ← ValidationIssue.message      (src/core/validation.ts)
 *
 * It is the only chip left. AutoChip (the valued "⚙ calculated for you" card)
 * and ContextChip/ContextRow (the section header's room · type · cars ·
 * sprinkler badges) were deleted by D-033 (R12, R14) — "This is just noise."
 *
 * It does not compute. A chip that assembles its own sentence out of counts is
 * a second implementation of a §4 rule or a §5 formula, free to disagree with
 * the one the estimate is priced from — that is precisely how the form and the
 * estimate drift apart.
 *
 * MathChip is an `aria-live` region: when a stepper tap flips
 * "4 + 2 = 6 of 6 cars accounted for" to "…5 — 1 car unaccounted for", the new
 * text is announced without the surveyor hunting for it.
 */

import type { ReactNode } from 'react';

import type { ValidationSeverity } from '../../core/types';

/* ───────────────────────────── MathChip ───────────────────────────── */

export type MathTone = 'ok' | 'bad' | 'warn' | 'info';

/** Severity → tone. `error` is the only one that stops a survey (§4 rule 11). */
export function toneForSeverity(severity: ValidationSeverity): MathTone {
  switch (severity) {
    case 'error':
      return 'bad';
    case 'warning':
      return 'warn';
    case 'info':
      return 'info';
  }
}

const MARKS: Record<MathTone, string> = {
  ok: '✓',
  bad: '✕',
  warn: '⚑',
  info: 'ℹ',
};

export interface MathChipProps {
  tone: MathTone;
  /** ValidationIssue.message, verbatim. */
  children: ReactNode;
  /** ValidationIssue.explanation, verbatim. */
  why?: string;
}

export function MathChip({ tone, children, why }: MathChipProps): ReactNode {
  return (
    <div className={`mathchip mathchip--${tone}`} role="status" aria-live="polite">
      <span aria-hidden="true">{MARKS[tone]}</span>
      <span>
        {children}
        {why ? <span className="mathchip__why">{why}</span> : null}
      </span>
    </div>
  );
}
